import {defineMessages, FormattedMessage} from 'react-intl';
import {useCallback, useEffect, useRef, useState} from 'react';

import {chevronDown, chevronUp} from '@sail/icons/react/Icon';
import {Icon, Link, view} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';
import {
  TopBar,
  useConversationUIFeatureFlags,
} from '@stripe-internal/conversations-ui';

import {CancelButton} from './CancelButton';
import {
  ConversationScreenshareButtonWithRequestAction,
  type ScreenshareRequestAction,
} from '../ConversationScreenshareButton';
import {Details} from './Details';
import {RACError} from './RACError';
import {useApp} from '../../../../core/hooks/appHooks';
import {useRACDetails} from '../../hooks/useRACDetails';

const messages = defineMessages({
  title: {
    id: 'conversations_assistant.requestACallTopBar.title',
    description: 'Title of the request a call top bar',
    defaultMessage: 'Have us call you',
  },
  showDetails: {
    id: 'conversations_assistant.requestACallTopBar.showDetails',
    description: 'Toggle button to show more details about call state',
    defaultMessage: 'Show details',
  },
  hideDetails: {
    id: 'conversations_assistant.requestACallTopBar.hideDetails',
    description: 'Toggle button to hide details about call state',
    defaultMessage: 'Hide details',
  },
});

type Props = {
  conversation: Conversation;
};

type ToggleDetailsProps = {
  onToggleDetails: () => void;
  showDetails: boolean;
};

type HeaderProps = ToggleDetailsProps & {
  isCancelLoading: boolean;
  onCancel: () => void;
  racStatus: ReturnType<typeof useRACDetails>['racStatus'];
  requestAction: ScreenshareRequestAction;
};

function ToggleDetails({onToggleDetails, showDetails}: ToggleDetailsProps) {
  return (
    <Link
      onPress={onToggleDetails}
      css={{
        font: 'label.small.emphasized',
      }}
    >
      <FormattedMessage
        {...(showDetails ? messages.hideDetails : messages.showDetails)}
      />
      <Icon
        icon={showDetails ? chevronUp : chevronDown}
        size="xxsmall"
        css={{marginLeft: 'xsmall'}}
      />
    </Link>
  );
}

function HeaderWithoutScreenshare({
  showDetails,
  onToggleDetails,
  isCancelLoading,
  onCancel,
  racStatus,
}: HeaderProps) {
  return (
    <view.div
      css={{
        font: 'label.medium.emphasized',
        stack: 'x',
        distribute: 'space-between',
      }}
    >
      <FormattedMessage {...messages.title} />
      <view.div>
        <ToggleDetails
          showDetails={showDetails}
          onToggleDetails={onToggleDetails}
        />
        <CancelButton
          isLoading={isCancelLoading}
          racStatus={racStatus}
          onCancel={onCancel}
        />
      </view.div>
    </view.div>
  );
}

function HeaderWithScreenshare({
  showDetails,
  onToggleDetails,
  isCancelLoading,
  onCancel,
  racStatus,
  requestAction,
}: HeaderProps) {
  return (
    <view.div
      css={{
        font: 'label.medium.emphasized',
        stack: 'y',
        gap: 'small',
      }}
    >
      <view.div css={{stack: 'x', distribute: 'space-between'}}>
        <FormattedMessage {...messages.title} />
        <ToggleDetails
          showDetails={showDetails}
          onToggleDetails={onToggleDetails}
        />
      </view.div>
      <view.div css={{stack: 'x', gap: 'small'}}>
        <ConversationScreenshareButtonWithRequestAction
          agentNames={racStatus === 'calling' ? ['Stripe Support'] : null}
          channel="rac"
          requestAction={requestAction}
        />
        <CancelButton
          screenshareRender
          isLoading={isCancelLoading}
          racStatus={racStatus}
          onCancel={onCancel}
        />
      </view.div>
    </view.div>
  );
}

function Header({
  showDetails,
  onToggleDetails,
  isCancelLoading,
  onCancel,
  racStatus,
  requestAction,
}: HeaderProps) {
  const {enableScreenshareUi} = useConversationUIFeatureFlags();

  if (enableScreenshareUi) {
    return (
      <HeaderWithScreenshare
        showDetails={showDetails}
        onToggleDetails={onToggleDetails}
        isCancelLoading={isCancelLoading}
        onCancel={onCancel}
        racStatus={racStatus}
        requestAction={requestAction}
      />
    );
  }

  return (
    <HeaderWithoutScreenshare
      showDetails={showDetails}
      onToggleDetails={onToggleDetails}
      isCancelLoading={isCancelLoading}
      onCancel={onCancel}
      racStatus={racStatus}
      requestAction={requestAction}
    />
  );
}

export function RACTopBar({conversation}: Props) {
  const app = useApp();
  const racDetails = useRACDetails(conversation);
  const requestAction = useCallback<ScreenshareRequestAction>(
    (name, data = {}, waitForResponse = false) => {
      return app.requestAction({
        action: {
          name,
          data,
        },
        waitForResponse,
      });
    },
    [app],
  );

  return (
    <RACTopBarContent
      conversation={conversation}
      racDetails={racDetails}
      requestAction={requestAction}
    />
  );
}

export function RACTopBarContent({
  conversation,
  racDetails,
  requestAction,
}: Props & {
  racDetails: ReturnType<typeof useRACDetails>;
  requestAction: ScreenshareRequestAction;
}) {
  const {
    userName,
    userPhoneNumber,
    error,
    loading,
    hasVerificationCodes,
    verificationCodes,
    racStatus,
    isCancelLoading,
    cancelRac,
    callEnded,
  } = racDetails;

  const leaveRef = useRef<boolean>(false);

  const [showDetails, setShowDetails] = useState<boolean>(true);
  const onToggleDetails = useCallback(() => {
    setShowDetails((value) => !value);
  }, []);
  const onCancel = useCallback(() => {
    cancelRac();
  }, [cancelRac]);

  useEffect(() => {
    if (leaveRef.current) {
      return;
    }

    if (callEnded && conversation) {
      leaveRef.current = true;
      conversation.leave();
    }
  }, [conversation, callEnded]);

  if (error) {
    return <RACError serverError={error} />;
  }

  return (
    <TopBar data-testId="widget-request-a-call">
      <view.div css={{stack: 'y'}}>
        <Header
          showDetails={showDetails}
          onToggleDetails={onToggleDetails}
          isCancelLoading={isCancelLoading}
          onCancel={onCancel}
          racStatus={racStatus}
          requestAction={requestAction}
        />
        {showDetails ? (
          <Details
            userName={userName}
            userPhoneNumber={userPhoneNumber}
            hasVerificationCodes={hasVerificationCodes}
            userCode={verificationCodes?.userCode || ''}
            agentCode={verificationCodes?.agentCode || ''}
            error={error}
            loading={loading}
          />
        ) : null}
      </view.div>
    </TopBar>
  );
}
